import type { KnowledgeBaseClient } from './knowledge-base-client';
import type { RAGDocument, BusinessInfo, ChatId } from '../../types';
import { type LLMMessage } from '@wtc/chat-types';
import { buildSystemPrompt, formatMessagesForAI } from '../prompts/prompt-builder';
import type { AskAlanV2Config } from '../../config/config-types';
import { 
  enhanceQueryWithContext, 
  rankDocumentsByRelevance,
  formatDocumentsWithAlanPerspective,
  createQueryMetadata
} from './query-enhancer';
import { logger } from '../logger';

/**
 * RAG Engine for retrieving context and building prompts
 * 
 * Implements three-tier memory model:
 * - Tier 1: Recent messages (last 10 turns)
 * - Tier 2: Conversation summary (preserved intent)
 * - Tier 3: Semantic memory + domain knowledge (Zilliz)
 */
export class RAGEngine {
  private knowledgeBase: KnowledgeBaseClient;
  private config: AskAlanV2Config;

  constructor(knowledgeBase: KnowledgeBaseClient, config: AskAlanV2Config) {
    this.knowledgeBase = knowledgeBase;
    this.config = config;
  }

  /**
   * Retrieve relevant context for a query
   * 
   * Enhances the query with strategic keywords and context before retrieval
   * to optimize embedding matching in Zillis Cloud
   */
  async retrieveRelevantContext(
    query: string,
    userId: string,
    locale: string = 'en'
  ): Promise<{
    knowledgeBaseDocs: RAGDocument[];
    userBusinessContext: BusinessInfo | null;
    contextText: string;
  }> {
    // Enhance query with strategic keywords for better embedding retrieval
    const enhancedQuery = enhanceQueryWithContext(query, userId, locale);
    
    logger.debug('query enhancement', {
      op: 'RAGEngine.retrieveRelevantContext',
      originalQuery: query,
      enhancedQuery: enhancedQuery,
    });
    
    // Retrieve context using enhanced query
    const context = await this.knowledgeBase.retrieveContext(enhancedQuery, userId);
    
    // Rank documents by relevance to WTC operations
    const rankedDocs = rankDocumentsByRelevance(context.knowledgeBaseDocs as any, query);
    
    // Convert ranked docs back to RAGDocument format
    const ragDocs: RAGDocument[] = rankedDocs.map((doc: any) => ({
      id: doc.id || `doc-${Math.random()}`,
      content: doc.content,
      source: doc.source || 'Expert Knowledge Base',
      score: doc.score,
      metadata: doc.metadata,
    }));
    
    logger.debug('document ranking', {
      op: 'RAGEngine.retrieveRelevantContext',
      originalCount: context.knowledgeBaseDocs.length,
      topRelevanceScore: (rankedDocs[0] as any)?.relevanceScore || 0,
    });
    
    // Format documents with Alan's perspective
    const formattedContext = formatDocumentsWithAlanPerspective(ragDocs, locale);
    
    return {
      knowledgeBaseDocs: ragDocs,
      userBusinessContext: context.userBusinessContext,
      contextText: formattedContext,
    };
  }

  /**
   * Build RAG prompt with context
   * 
   * Uses three-tier memory model:
   * - Tier 1: Recent messages (last 10 turns)
   * - Tier 2: Conversation summary (preserved intent)
   * - Tier 3: Semantic memory + domain knowledge (Zilliz)
   * 
   * IMPORTANT: conversationHistory parameter is already in LLMMessage format (text-only).
   * Do NOT call formatMessagesForAI() on it - the messages are already converted!
   * Calling formatMessagesForAI() on LLMMessage[] will lose content.
   * 
   * Enhances queries with strategic keywords to optimize embedding retrieval
   */
  async buildRAGPrompt(
    query: string,
    userId: string,
    conversationHistory: LLMMessage[],
    conversationId?: ChatId,
    locale: string = 'en',
    entityContext?: { entityType: string; entityId: string; entityDisplayName: string; entityData?: Record<string, any> }
  ): Promise<{
    systemPrompt: string;
    messages: LLMMessage[];
    conversationSummary?: string;
  }> {
    const ragLogger = logger.child({ op: 'RAGEngine.buildRAGPrompt', userId });
    ragLogger.debug('buildRAGPrompt called', { query, locale, hasEntityContext: !!entityContext });

    // Retrieve context with query enhancement
    ragLogger.debug('retrieving context from knowledge base');
    const context = await this.retrieveRelevantContext(query, userId, locale);

    ragLogger.debug('context retrieved', {
      docCount: context.knowledgeBaseDocs.length,
      contextLength: context.contextText.length,
    });

    // TIER 2: Retrieve conversation summary if available
    let conversationSummary: string | null = null;
    if (conversationId) {
      try {
        ragLogger.debug('retrieving conversation summary');
        // Conversation summaries are non-critical for RAG
        ragLogger.debug('conversation summary retrieval skipped (non-critical)');
      } catch (error) {
        ragLogger.warn('failed to retrieve conversation summary', {
          error: error instanceof Error ? error.message : String(error),
        });
        // Continue without summary if retrieval fails
      }
    }

    // Build system prompt with Tier 2 summary
    ragLogger.debug('building system prompt');
    const systemPrompt = buildSystemPrompt(
      this.config.prompts,
      { userId },
      context.userBusinessContext,
      context.knowledgeBaseDocs,
      conversationSummary || undefined,
      locale,
      entityContext
    );

    ragLogger.debug('system prompt built', {
      promptLength: systemPrompt.length,
      docCount: context.knowledgeBaseDocs.length,
    });

    // TIER 1: Use only recent messages (last 10 turns)
    // This prevents token overflow and keeps costs predictable
    // NOTE: conversationHistory is already in LLMMessage format (text-only)
    // Do NOT call formatMessagesForAI again - it will lose content!
    const recentMessages = conversationHistory.slice(-10);
    const messages = recentMessages; // Already in LLM format, just slice

    ragLogger.debug('RAG prompt complete', {
      messageCount: messages.length,
      systemPromptLength: systemPrompt.length,
    });

    return {
      systemPrompt,
      messages,
      conversationSummary: conversationSummary || undefined,
    };
  }
}


